import { useMutation, useQueryClient } from "@tanstack/react-query";
import { configuration } from "src/configuration";
import { useSignalR } from "src/hooks";
import { EditTaskItemContextDto, FetchError, queryKeys, useAccessToken } from "src/http";

type Variables = {
  taskItemGuid: string;
  contextActionItemGuid: string | null;
};

const useEditTaskItemContextRequest = () => {
  const { getAccessToken } = useAccessToken();

  async function editTaskItemContextRequest(variables: Variables) {
    const accessToken = await getAccessToken();
    const body: EditTaskItemContextDto = {
      contextActionItemGuid: variables.contextActionItemGuid,
    };

    const response = await fetch(`${configuration.apiRootUrl}/task-items/${variables.taskItemGuid}/context`, {
      headers: {
        Authorization: `Bearer ${accessToken}`,
        "Content-Type": "application/json",
      },
      method: "put",
      body: JSON.stringify(body),
    });

    if (!response.ok) {
      throw new FetchError(response);
    }
  }

  return { editTaskItemContextRequest };
};

const useEditTaskItemContext = () => {
  const { editTaskItemContextRequest } = useEditTaskItemContextRequest();
  const queryClient = useQueryClient();
  const signalR = useSignalR();

  return useMutation({
    mutationFn: editTaskItemContextRequest,
    onSuccess: (_, variables) => {
      signalR.sendRefetchTaskItem(variables.taskItemGuid);
      signalR.sendRefetchComments(variables.taskItemGuid);
      signalR.sendRefetchTeamTaskItems();
      signalR.sendRefetchUserTaskItems();

      if (variables.contextActionItemGuid) {
        signalR.sendRefetchActionItem(variables.contextActionItemGuid);
        // todo: do we need to update the old context?
      }
    },
    onSettled: (_data, _error, variables) => {
      queryClient.refetchQueries(queryKeys.taskItems.getTaskItemDetail(variables.taskItemGuid));
      queryClient.refetchQueries(queryKeys.taskItems.getTaskItemFeed(variables.taskItemGuid));

      queryClient.invalidateQueries(queryKeys.taskItems.allTeamTaskItems);
      queryClient.invalidateQueries(queryKeys.taskItems.allTeamTaskItemSubtasks);
      queryClient.invalidateQueries(queryKeys.taskItems.allUserTaskItems);

      if (variables.contextActionItemGuid) {
        queryClient.refetchQueries(queryKeys.actionItems.getActionItemDetail(variables.contextActionItemGuid));
        // todo: do we need to update the old context?
      }
    },
  });
};

export { useEditTaskItemContext };
